<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges"></alert-unsaved-changes>
    <v-progress-linear
      v-show="loading || providerLoading"
      color="neutral400"
      indeterminate
      rounded
      height="3"
    ></v-progress-linear>
    <v-form
      v-show="!loading && !providerLoading"
      ref="form"
      v-model="valid"
      :lazy-validation="false"
    >
      <v-row>
        <v-col cols="5">
          <label>{{ $t('scientific-domain') }}</label>
          <p class="label-info">{{ $t('scientific-domain-message') }}</p>
        </v-col>
        <v-col cols="7">
          <label>{{ $t('scientific-subdomain') }}</label>
          <p class="label-info">{{ $t('scientific-subdomain-message') }}</p>
        </v-col>
      </v-row>
      <v-row v-for="(scientific, i) in scientifics" :key="i">
        <v-col cols="5">
          <v-autocomplete
            v-model="scientific.domain"
            :items="domains"
            outlined
            :rules="requiredRule()"
            :disabled="!edit"
            class="required"
            :placeholder="$t('please-select-domain')"
            data-cy="scientificDomain"
            v-on:change="filterSubDomains(i)"
          ></v-autocomplete>
        </v-col>
        <v-col cols="5">
          <v-autocomplete
            v-model="scientific.subDomain"
            :items="scientific.selected"
            :item-value="'id'"
            :item-text="'subDomain'"
            return-object
            outlined
            :disabled="scientific.domain == null || !edit"
            :rules="requiredRule()"
            :placeholder="$t('please-select-subdomain')"
            data-cy="scientificDomain"
          ></v-autocomplete>
        </v-col>
        <v-col cols="2">
          <v-btn fab small text @click="removeDomain(i)">
            <v-icon>
              $vuetify.icons.trash
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-row class="links" v-if="edit">
        <v-col cols="12" sm="12" md="6">
          <v-btn tile text @click="addScientific()">
            <v-icon left>
              $vuetify.icons.plus
            </v-icon>
            <span class="text-decoration-underline text-transform-none font-weight-bold">
              {{ $t('add-scientific') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" v-if="!next">
          <v-expansion-panels>
            <v-expansion-panel :key="1">
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pt-10 pb-7">
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('tags') }}</label>
                    <p class="label-info">{{ $t('tags-message') }}</p>
                    <v-combobox
                      v-model="provider.tags"
                      clearable
                      :placeholder="$t('tags')"
                      multiple
                      persistent-hint
                      small-chips
                      return-object
                      outlined
                      :disabled="!edit"
                      data-cy="tags"
                      hint="Type to create a new tag"
                    >
                      <template v-slot:no-data>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>
                              Press <kbd>enter</kbd> to create a new one
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <template v-slot:selection="{ attrs, item, parent, selected }">
                        <v-chip
                          v-bind="attrs"
                          :input-value="selected"
                          :close="true"
                          @click:close="removeChip(item)"
                        >
                          <span class="pr-2">
                            {{ item }}
                          </span>
                        </v-chip>
                      </template>
                    </v-combobox>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('structure-type') }}</label>
                    <p class="label-info">{{ $t('structure-type-message') }}</p>
                    <v-autocomplete
                      v-model="provider.structureTypes"
                      :items="structureTypes"
                      :placeholder="$t('structure-type')"
                      :item-value="'id'"
                      :item-text="'name'"
                      return-object
                      outlined
                      clearable
                      :disabled="!edit"
                      multiple
                      data-cy="structureTypes"
                    ></v-autocomplete>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-form>
    <!--BUTTONS-->
    <v-row class="mt-10" v-if="next">
      <v-col cols="6" class="d-flex justify-end">
        <v-btn
          depressed
          rounded
          x-large
          color="black"
          @click="back()"
          outlined
          class="black--text font-weight-bold"
          data-cy="submit"
        >
          <v-icon color="black">$vuetify.icons.back</v-icon>
          {{ $t('back') }}
        </v-btn>
      </v-col>
      <v-col cols="6">
        <v-btn
          depressed
          rounded
          x-large
          color="primary200"
          @click="add()"
          :disabled="!valid || !scientifics.length"
          class="white--text font-weight-bold"
          data-cy="submit"
          >{{ $t('next') }} <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row class="center mt-10" v-if="edit && !next">
      <v-col cols="6" class="d-flex justify-end" v-if="!admin">
        <v-btn
          depressed
          rounded
          x-large
          color="black"
          @click="cancel()"
          outlined
          class="black--text font-weight-bold"
          data-cy="submit"
        >
          {{ $t('cancel') }}
        </v-btn>
      </v-col>
      <v-col :cols="admin ? '12' : '6'" :class="admin ? 'text-center' : ''">
        <v-btn
          depressed
          rounded
          x-large
          color="neutral"
          @click="add()"
          :disabled="!valid || !scientifics.length"
          class="white--text font-weight-bold"
          data-cy="submit"
          >{{ $t('update') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';
import util from '@/mixins/util.js';
import base from '@/services/base-service.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';

export default {
  name: 'ProviderClassificationInfo',
  mixins: [util, base],
  props: {
    admin: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Boolean,
      default: true,
    },
    next: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    AlertUnsavedChanges,
  },
  data() {
    return {
      valid: false,
      loadingComponents: -3,
      unsavedChanges: false,
      provider: {
        id: null,
        tags: null,
        scientificsIds: [],
        structureType: [],
        scientifics: [],
      },
      scientifics: [],
      domains: [],
      subdomains: [],
      structureTypes: [],
      providerLoading: true,
    };
  },
  mounted() {
    this.getStructureTypes().then(res => {
      this.loadingComponents++;
      this.structureTypes = res.data.content;
    });
    this.getScientificDomains().then(res => {
      this.loadingComponents++;
      this.domains = res.data.content;
    });
    this.getScientificSubdomains().then(res => {
      this.loadingComponents++;
      this.subdomains = res.data.content;
    });
  },
  methods: {
    cancel() {
      this.$emit('cancel', true);
    },
    addDefaultValue(i) {
      //add Generic as Default
      this.scientifics[i].selected = this.subdomains.filter(
        item => item.domain === this.scientifics[i].domain,
      );
      this.scientifics[i].subDomain = this.scientifics[i].selected[0];
    },
    back() {
      console.log('back!');
      this.$emit('tab', 1);
    },
    addScientific() {
      this.scientifics.push({ domain: 'Generic', subDomain: null, selected: [] });
      this.addDefaultValue(this.scientifics.length - 1);
    },
    removeDomain(index) {
      this.scientifics.splice(index, 1);
    },
    add() {
      EventBus.$emit('waiting', true);
      this.provider.scientificsIds = [];
      this.scientifics.forEach(s => {
        this.provider.scientificsIds.push(s.subDomain.id);
      });
      this.axios
        .post('/api/v1/provider-settings/classification-info', this.provider)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('provider-updated'),
          });
          this.unsavedChanges = false;
          this.$emit('tab', 3);
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    removeChip(itemText) {
      this.provider.tags.forEach(obj => {
        if (obj === itemText) {
          this.provider.tags.splice(this.provider.tags.indexOf(obj), 1);
        }
      });
      this.provider.tags = [...this.provider.tags];
    },
    filterSubDomains(i) {
      console.log('[Domain] Domain changed');
      this.scientifics[i].subDomain = null;
      this.scientifics[i].selected = this.subdomains.filter(
        item => item.domain === this.scientifics[i].domain,
      );
    },
    getProvider(id) {
      EventBus.$emit('waiting', true);
      this.axios
        .get('/api/v1/provider-settings/' + id)
        .then(res => {
          this.provider = res.data;
          this.provider.scientifics.forEach(scientific => {
            let subScientifics = this.subdomains.filter(item => item.domain === scientific.domain);
            this.scientifics.push({
              selected: subScientifics,
              domain: scientific.domain,
              subDomain: scientific,
            });
          });
          this.$nextTick(() => {
            this.providerLoading = false;
          });
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
  },
  computed: {
    loading() {
      return this.loadingComponents !== 0;
    },
  },
  watch: {
    loading(value) {
      let id = this.$route.params.id;
      if (value === false) {
        console.log('Loading');
        if (sessionStorage.getItem('providerId')) {
          this.getProvider(sessionStorage.getItem('providerId'));
        } else if (id != null) {
          this.getProvider(id);
        } else {
          this.$nextTick(() => {
            this.providerLoading = false;
          });
        }
      }
    },
    provider: {
      handler: function(val, oldVal) {
        console.log('[Provider] Value changed');
        if (oldVal.id) {
          this.unsavedChanges = true;
        }
      },
      deep: true,
    },
  },
};
</script>
